import { DeliveryItems } from '@/api';
import { AppRoutes } from '@/app/routers';
import { useAppDispatch } from '@/app/store';
import { ModalInfo } from '@/components/layout/ModalInfo';
import { UIBox } from '@/components/ui/Box';
import { Typography } from '@/components/ui/Typography';
import {
  ReceivingProcessConst,
  setDeliveryProcess,
} from '@/features/deliveries/deliveriesSlice';
import { useSelector } from '@/hooks/useSelector';
import { colors } from '@/theme';
import { FormControl, Radio, RadioGroup, TextField } from '@material-ui/core';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';

const StyledRadioWrapper = styled(UIBox)`
  display: flex;
  flex-direction: column;
  padding-top: 16px;
  gap: 8px;
`;

const StyledRadioGroup = styled(RadioGroup)`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

type ModalVariant = 'partial' | 'new' | 'multiple';

type InfoModalState = {
  variant: ModalVariant;
  title: string;
  subtitle: string;
  message?: string;
};

type InfoModalProps = {
  selectedDeliveries: DeliveryItems[];
  open: boolean;
  setOpen: (open: boolean) => void;
};

// type ReceivingProcess = ReceivinProcessType | undefined;

// ---------------------------------------------------------------------------
// BoxCountInput: asks how many boxes arrived and preselects the process.
// ---------------------------------------------------------------------------
const BoxCountInput = ({
                         expectedBoxes,
                         onBoxCountChange,
                       }: {
  expectedBoxes: number;
  onBoxCountChange: (received: number | undefined) => void;
}): JSX.Element => {
  const { t } = useTranslation();
  const [value, setValue] = useState<string>('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const raw = e.target.value;
    // Allows a single "0", but not "0001" or "007".
    const normalized = raw.replace(/^0+(\d+)/, '$1');
    setValue(normalized);
    const parsed = parseInt(normalized, 10);
    onBoxCountChange(isNaN(parsed) || parsed < 0 ? undefined : parsed);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (['e', 'E', '+', '-', '.', ','].includes(e.key)) {
      e.preventDefault();
    }
  };

  const handleOnInput = (e: React.FormEvent<HTMLInputElement>): void => {
    const input = e.target as HTMLInputElement;
    if (input.value.length > 4) {
      input.value = input.value.slice(0, 4);
    }
  };

  return (
    <UIBox
      flexDirection="column"
      gridGap={8}
      border={`1px solid ${colors.lightGrey}`}
      borderRadius={'8px'}
      padding={2}
      width={'100%'}
    >
      <Typography>{t('receiving.new.boxCount.label')}</Typography>
      <UIBox alignItems={'center'}>
        <TextField
          type="number"
          variant="outlined"
          size="small"
          inputProps={{ min: 0 }}
          value={value}
          onChange={handleChange}
          placeholder={t('receiving.new.boxCount.placeholder')}
          style={{ width: 140 }}
          onKeyDown={handleKeyDown}
          onInput={handleOnInput}
        />
        <Typography margin={"0 0 0 8px"}>
          {t('receiving.new.boxCount.expected.label', {
            count: expectedBoxes,
          })}
        </Typography>
      </UIBox>
      <Typography font="light" size={'sm'}>
        {t('receiving.new.boxCount.hint')}
      </Typography>
    </UIBox>
  );
};

// ---------------------------------------------------------------------------

const ReceivingProcessContent = ({
  expectedBoxes,
  onBoxCountChange,
}: {
  expectedBoxes: number;
  onBoxCountChange: (received: number | undefined) => void;
}): JSX.Element => {
  const { t } = useTranslation();
  const { deliveryProcess } = useSelector(state => state.deliveries);

  const radioButtonsLabel = [
    {
      label: t('receiving.new.radio.label.fullReceiving'),
      subtitle: t('receiving.new.radio.label.fullReceiving.subtitle'),
      value: ReceivingProcessConst.RCC,
      disabled:
        !deliveryProcess || deliveryProcess === ReceivingProcessConst.RCP,
    },
    {
      label: t('receiving.new.radio.label.partialReceiving'),
      subtitle: t('receiving.new.radio.label.partialReceiving.subtitle'),
      value: ReceivingProcessConst.RCP,
      disabled:
        !deliveryProcess || deliveryProcess === ReceivingProcessConst.RCC,
    },
  ];

  return (
    <UIBox flexDirection={'column'} gridGap={24} width={'100%'}>
      {/* New field: how many boxes were received. */}
      <BoxCountInput
        expectedBoxes={expectedBoxes}
        onBoxCountChange={onBoxCountChange}
      />

      <FormControl>
        <StyledRadioGroup
          aria-labelledby="demo-radio-buttons-group-label"
          name="radio-buttons-group"
          value={deliveryProcess ?? ''}
        >
          {radioButtonsLabel.map(radio => (
            <FormControlLabel
              key={radio.value}
              value={radio.value}
              control={<Radio color="primary" />}
              label={
                <StyledRadioWrapper>
                  <Typography color={radio.disabled ? 'disabled' : undefined}>
                    {radio.label}
                  </Typography>
                  <Typography
                    color={radio.disabled ? 'disabled' : undefined}
                    font="light"
                  >
                    {radio.subtitle}
                  </Typography>
                </StyledRadioWrapper>
              }
              disabled={radio.disabled}
            />
          ))}
        </StyledRadioGroup>
      </FormControl>
    </UIBox>
  );
};

const ModalNewDelivery = ({
  selectedDeliveries,
  open,
  setOpen,
}: InfoModalProps): JSX.Element => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { deliveryProcess } = useSelector(state => state.deliveries);

  const [infoModalState, setInfoModalState] = useState<
    InfoModalState | undefined
  >(undefined);

  // Expected boxes from the selected single delivery (only for the 'new' variant).
  const expectedBoxes: number = selectedDeliveries?.[0]?.boxes?.length ?? 0;

  const goToReceivingDeliveries = useCallback(() => {
    history.push(AppRoutes.RECEIVING_DELIVERIES);
  }, [history]);

  // Called by BoxCountInput: preselects the process based on the quantity.
  const handleBoxCountChange = useCallback(
    (received: number | undefined): void => {
      if (received === undefined) {
        // No valid value: go back to the default selection (no selection).
        dispatch(setDeliveryProcess(undefined));
        return;
      }
      if (received >= expectedBoxes) {
        dispatch(setDeliveryProcess(ReceivingProcessConst.RCC));
      } else {
        dispatch(setDeliveryProcess(ReceivingProcessConst.RCP));
      }
    },
    [dispatch, expectedBoxes]
  );

  const handleClose = useCallback((): void => {
    dispatch(setDeliveryProcess(undefined));
    setOpen(false);
  }, [dispatch, setOpen]);

  const handleSelectedDeliveries = useCallback((): void => {
    let nextModalState: InfoModalState | undefined;

    if (selectedDeliveries.length === 1) {
      const delivery = selectedDeliveries[0];

      if (delivery.status === 'Partial') {
        nextModalState = {
          variant: 'partial',
          title: t('receiving.partial.title'),
          subtitle: t('receiving.partial.subtitle'),
          message: t('receiving.partial.message'),
        };
      } else if (delivery.status === 'New') {
        nextModalState = {
          variant: 'new',
          title: t('receiving.new.title'),
          subtitle: t('receiving.new.subtitle'),
        };
      }
    } else {
      dispatch(setDeliveryProcess(ReceivingProcessConst.RCC));

      nextModalState = {
        variant: 'multiple',
        title: t('receiving.multiple.title'),
        subtitle: t('receiving.multiple.subtitle'),
        message: t('receiving.multiple.message'),
      };
    }

    setInfoModalState(nextModalState);
  }, [
    selectedDeliveries,
    dispatch,
    t,
  ]);

  useEffect(() => {
    if (open) handleSelectedDeliveries();
  }, [open, handleSelectedDeliveries]);

  const content =
    infoModalState?.variant === 'new' ? (
      <ReceivingProcessContent
        expectedBoxes={expectedBoxes}
        onBoxCountChange={handleBoxCountChange}
      />
    ) : undefined;

  return (
    <ModalInfo
      open={open}
      onClose={handleClose}
      title={infoModalState?.title}
      subtitle={infoModalState?.subtitle}
      message={infoModalState?.message}
      content={content}
      onConfirmClick={goToReceivingDeliveries}
      onBackClick={handleClose}
      disableConfirm={!deliveryProcess}
    />
  );
};

export default ModalNewDelivery;
